<template>
    <div class="card col">
        <div class="card-body">
            <form>
                <slider-input 
                    v-for="input in inputs"
                    :key="input.sliderKey"
                    :label="input.label"
                    :label-suffix="input.labelSuffix"
                    :initial-value="input.initialValue"
                    :min-value="input.minValue"
                    :min-label="input.minLabel"
                    :max-value="input.maxValue"
                    :max-label="input.maxLabel"
                    :increment="input.increment"
                    :slider-key="input.sliderKey"
                    :is-fixed-amount="isFixedAmount"
                    @changed="input.updateFunction"
                />
                <div v-if="$isSweden()" class="row">
                    <div class="col-md-6 col-12 mb-3">
                        <label class="form-label fw-bold">
                            {{ $translate('calculator_arrangement_fee', 'Uppläggningsavgift (Max 1000 kr)') }}
                        </label>
                        <input 
                            id="amount" 
                            v-model.number="newArrangementFee" 
                            type="number" 
                            class="form-control form-control-lg" 
                            name="arrangementFee" 
                            placeholder="0"
                            v-on="checkArrangementFee()"
                        >
                    </div>
                    <div class="col-md-6 col-12 mb-3">
                        <label class="form-label fw-bold">
                            {{ $translate('calculator_invoice_fee', 'Aviavgift (Max 50 kr / månad)') }}
                        </label>
                        <input 
                            id="amount" 
                            v-model.number="newInvoiceFee" 
                            type="number" 
                            class="form-control form-control-lg" 
                            name="invoiceFee" 
                            placeholder="0"
                            v-on="checkInvoiceFee()"
                        >
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
<script>
import SliderInput from './SliderInput.vue';
import { pick } from 'lodash';

export default {
    components: {
        SliderInput,
    },
    props: {
        isCarCalculator: {
            type: Boolean,
            required: false,
            default: false
        },
        isFixedAmount: {
            type: Boolean,
            required: false
        },
        initialInputValues: {
            type: Object,
            required: true
        },
        minMaxValues: {
            type: Object,
            required: true
        },
        carCostLabel: {
            type: String,
            required: false,
            default: ''
        },
    },
    data() {
        return {
            newAmount: this.initialInputValues.amount,
            newDuration: this.initialInputValues.duration,
            newInterestRate: this.initialInputValues.interestRate,
            newArrangementFee: this.initialInputValues.arrangementFee === 0 ? null : this.initialInputValues.arrangementFee,
            newInvoiceFee: this.initialInputValues.invoiceFee === 0 ? null : this.initialInputValues.invoiceFee,
            newDownPayment: this.initialInputValues.downPayment,
            newCarCost: this.initialInputValues.carCost,

            interestKeyName: this.$isSpain() ? 'preamble_effective_interest' : 'interest', 
            interestTitle: this.$isSpain() ? 'Effektiv ränta' : 'Ränta',
        };
    },
    computed: {
        values() {
            return {
                amount: this.newAmount,
                duration: this.newDuration,
                interestRate: this.newInterestRate,
                arrangementFee: Number(this.newArrangementFee),
                invoiceFee: Number(this.newInvoiceFee),
                downPayment: this.newDownPayment,
                carCost: this.newCarCost,
            };
        },
        calculateIncrement() {
            const range = (this.minMaxValues.maxAmount - this.minMaxValues.minAmount);

            if (range > 100000){
                return 1000;
            }
            if (range > 60000){
                return 100;
            }
            return 50;
        },
        inputs() {
            const inputsConfig = {
                loanAmount: {
                    label: this.$translate('calculator_loan_amount', 'Lånebelopp'),
                    labelSuffix: this.$getCurrencySymbol(),
                    initialValue: this.initialInputValues.amount,
                    minValue: this.minMaxValues.minAmount,
                    minLabel: this.$format.currency(this.minMaxValues.minAmount) || '',
                    maxValue: this.minMaxValues.maxAmount,
                    maxLabel: this.$format.currency(this.minMaxValues.maxAmount) || '',
                    increment: this.calculateIncrement,
                    sliderKey: 'amount',
                    updateFunction: (amount) => this.newAmount = amount,
                },
                duration: {
                    label: this.$translate('loan_duration', 'Löptid'),
                    labelSuffix: this.newDuration === 1 
                        ? this.$translate('month', 'månad') 
                        : this.$translate('months', 'månader'),
                    initialValue: this.initialInputValues.duration,
                    minValue: this.minMaxValues.minDuration,
                    minLabel: `${this.minMaxValues.minDuration} ${this.minMaxValues.minDuration === 1 
                        ? this.$translate('month', 'månad') 
                        : this.$translate('months', 'månader')}`,
                    maxValue: this.minMaxValues.maxDuration,
                    maxLabel: this.minMaxValues.maxDuration === 180 
                        ? this.$translate('calculator_loan_max_duration', '180 månader (15 år)') 
                        : `${this.minMaxValues.maxDuration} ${this.$translate('months', 'månader')}`,
                    increment: 1,
                    sliderKey: 'duration',
                    updateFunction: (duration) => this.newDuration = duration,
                },
                interest: {
                    label: this.$translate(this.interestKeyName, this.interestTitle),
                    labelSuffix: '%',
                    initialValue: this.initialInputValues.interestRate,
                    minValue: this.minMaxValues.minInterest,
                    minLabel: `${this.minMaxValues.minInterest}%`,
                    maxValue: this.minMaxValues.maxInterest,
                    maxLabel: `${this.minMaxValues.maxInterest}%`,
                    increment: 1,
                    sliderKey: 'interestRate',
                    updateFunction: (interestRate) => this.newInterestRate = interestRate,
                },
                carCost: {
                    label: this.carCostLabel,
                    labelSuffix: this.$getCurrencySymbol(),
                    initialValue: this.initialInputValues.carCost,
                    minValue: this.minMaxValues.minAmount,
                    minLabel: this.$format.currency(this.minMaxValues.minAmount) || '',
                    maxValue: this.minMaxValues.maxAmount,
                    maxLabel: this.$format.currency(this.minMaxValues.maxAmount) || '',
                    increment: this.calculateIncrement,
                    sliderKey: 'carCost',
                    updateFunction: (carCost) => {
                        this.newCarCost = carCost;
                        this.newAmount = this.getCarLoanAmount();
                    },
                },
                downPayment: {
                    label: this.$translate('calculator_car_down_payment', 'Kontantinsats'),
                    labelSuffix: '%',
                    initialValue: this.initialInputValues.downPayment,
                    minValue: this.minMaxValues.minDownPayment,
                    minLabel: `${this.minMaxValues.minDownPayment}%`,
                    maxValue: this.minMaxValues.maxDownPayment, 
                    maxLabel: `${this.minMaxValues.maxDownPayment}%`,
                    increment: 1,
                    sliderKey: 'downPayment',
                    updateFunction: (downPayment) => {
                        this.newDownPayment = downPayment;
                        this.newAmount = this.getCarLoanAmount();
                    },
                },
            };
            return this.isCarCalculator 
                ? pick(inputsConfig, ['carCost', 'downPayment', 'interest', 'duration']) 
                : pick(inputsConfig, ['loanAmount', 'duration', 'interest']);
        }
    },
    watch: {
        values() {
            this.$emit('changed', this.values);
        },
    },
    methods: {
        checkArrangementFee() {
            if (this.newArrangementFee > 1000) {
                this.newArrangementFee = 1000;
            }
        },
        checkInvoiceFee() {
            if (this.newInvoiceFee > 50) {
                this.newInvoiceFee = 50;
            }
        },
        getCarLoanAmount() {
            return this.newCarCost * (1 - this.newDownPayment/100);
        },
    }
};
</script>